'use strict';

const { parse: parseUrl } = require('url');

/**
 * Shorten a string, to avoid exposing, for example, full tokens in logs.
 *
 * @param {string} s the string to shorten
 * @returns {string} a shortened substring of s
 */
const redactParts = s => s && s.substring && s.substring(0, Math.min(s.length / 2, 32));

/**
 * JSON Web Token regex pattern
 *
 * Repeated twice: eyJ followed by base64 url encoded string followed by period
 * Followed by base64 url encoded string
 *
 * The signature (last base64 url encoded string) may be empty for unsecured JWTs
 *
 * https://tools.ietf.org/html/rfc7519
 */
const JWT_REGEX = /(eyJ[a-zA-z0-9-_]+\.){2}[a-zA-z0-9-_]*/g;

/**
 * Any non-alphanumeric (or %2F for url encoded)
 *  64 alphanumerics (token)
 * Followed by any non-alphanumeric
 */
const API_TOKEN_REGEX = new RegExp(
  `\
([^0-9a-zA-Z]|(?:%2F))\
([0-9a-zA-Z]{64})\
([^0-9a-zA-Z]|$)\
`,
  'g'
);

const SIGNED_TOKEN_FORMAT = /(ATT[A-Z])([A-Za-z0-9/-]{16,})[A-F0-9]{8}/g;

/**
 *
 * device or devices /
 * followed by 100+ alphanumeric or :-_
 */
const DEVICE_TOKEN_REGEX = new RegExp(`(devices?/)([0-9a-zA-Z:_-]{100,})`, 'g');

/**
 * Any non-alphanumeric (or %2F for url encoded)
 * Followed etiher by
 *  24-24-32 (invitation tokens)
 *  24-13-32 (password reset tokens)
 *  invite url followed by 32 alphanumerics
 *  invitationSecret url followed by 32 alphanumerics
 * Followed by any non-alphanumeric
 */
const INVITATION_TOKEN_REGEX = new RegExp(
  `\
([^0-9a-zA-Z]|(?:%2F))\
(\
[0-9a-zA-Z]{24}[-/][0-9a-zA-Z]{24}[-/][0-9a-zA-Z]{32}|\
[0-9a-zA-Z]{24}[-/][0-9]{13}[-/][0-9a-zA-Z]{32}|\
invite/(?:b/)?[^/]+/[0-9a-zA-Z]{32}|\
invitationSecret/[0-9a-zA-Z]{32}\
)\
([^0-9a-zA-Z]|$)\
`,
  'g'
);

const TRUNCATION_MESSAGE = '[TOKEN_TRUNCATED_BY_URL_LIBRARY]';
const UGC_MESSAGE = '[UGC_TRUNCATED_BY_URL_LIBRARY]';

// remove Tokens/Secrets and token looking things if sanitizeData = true
// always shallowCopy the object
function removeTokenValue(object, sanitizeData = true, seen = []) {
  if (!object) {
    return object;
  }
  if (typeof object === 'string') {
    let redacted = object;
    if (sanitizeData) {
      redacted = redacted.replace(/([a-z0-9]{32})[a-z0-9]{32}/g, '$1');
      redacted = redacted.replace(/secret=[0-9a-f]+/gi, 'secret=<SECRET>');
      // Bearer tokens
      redacted = redacted.replace(/Bearer [A-Za-z0-9\-._+/]+/gi, '<BEARER>');
      // API tokens
      redacted = redacted.replace(API_TOKEN_REGEX, (match, pre, token, post) => {
        return `${pre}${redactParts(token)}${TRUNCATION_MESSAGE}${post}`;
      });
      redacted = redacted.replace(
        SIGNED_TOKEN_FORMAT,
        (match, prefix, token) => `${prefix}${redactParts(token)}${TRUNCATION_MESSAGE}`
      );
      // Invitation tokens in URL
      redacted = redacted.replace(INVITATION_TOKEN_REGEX, (match, pre, invitationToken, post) => {
        const char = invitationToken.includes('/') ? '/' : '-';
        const anonymousToken =
          invitationToken
            .split(char)
            .slice(0, -1)
            .join(char) + char;
        return `${pre}${anonymousToken}${TRUNCATION_MESSAGE}${post}`;
      });
      // UGC in Search Query
      try {
        const { pathname, query } = parseUrl(redacted, { parseQueryString: true });
        if (pathname === '/1/search') {
          const queryString = query.query;
          if (queryString !== null) {
            const encoded = encodeURIComponent(queryString);
            if (redacted.includes(`query=${encoded}`)) {
              redacted = redacted.replace(`query=${encoded}`, `query=${UGC_MESSAGE}`);
            } else {
              redacted = redacted.replace(`query=${queryString}`, `query=${UGC_MESSAGE}`);
            }
          }
        }
      } catch {
        // Malformed or not a url.
      }
      // JWT
      redacted = redacted.replace(JWT_REGEX, TRUNCATION_MESSAGE);
      // DEVICE TOKEN
      redacted = redacted.replace(
        DEVICE_TOKEN_REGEX,
        (match, pre, token) => `${pre}${redactParts(token)}${TRUNCATION_MESSAGE}`
      );
      // attempt at a generic catchall
      redacted = redacted.replace(/(^|\W)([0-9a-f]{64})(\W|$)/gim, '$1[REDACTED]$3');
      // Anonymize emails
      redacted = anonymizeEmailsInString(redacted);
      // Anonymize ips
      redacted = redacted.replace(/(\d+\.\d+)(\.\d+){2}/g, '$1.*.*');
    }
    return redacted;
  }
  if (seen.indexOf(object) !== -1) {
    return '__circular__';
  }
  seen.push(object);
  const copy = {};
  Object.getOwnPropertyNames(object).forEach(function(prop) {
    let value = object[prop];
    switch (prop) {
      case 'token':
        if (value) {
          value = removeTokenValue(value, sanitizeData, seen);
        }
        break;
      case 'secret':
        if (value) {
          value = sanitizeData ? '<SECRET>' : value;
        }
        break;
      case 'Authorization':
        // Redact Bearer tokens
        if (value && typeof value === 'string' && value.startsWith('Bearer ')) {
          value = sanitizeData ? '<BEARER>' : value;
        }
        break;
      default:
        break;
    }
    switch (typeof value) {
      case 'object':
        if (value) {
          value = removeTokenValue(value, sanitizeData, seen);
        }
        break;
      case 'string':
        value = removeTokenValue(value, sanitizeData);
        break;
      default:
        break;
    }
    copy[prop] = value;
  });
  return copy;
}

/**
 * Extract the domain from an email address.
 *
 * @param {string} email the email address
 * @returns {string} the domain, or null if the domain could not be extracted
 */

const getDomain = email => {
  if (!email) {
    return null;
  }

  const lastAt = email.lastIndexOf('@');

  if (lastAt === -1) {
    return null;
  }

  return email.slice(lastAt + 1) || null;
};

/**
 * Anonymize an email for logging; ensure we don't log the entire email address.
 * @param {string} email the email to anonymize
 * @returns {string} the anonymized email address
 */
const anonymizeEmail = email => {
  if (!email || typeof email !== 'string') {
    return email;
  }
  const domain = getDomain(email);
  const emailPrefix = email.slice(0, email.lastIndexOf('@'));
  const lettersToKeep = Math.floor(emailPrefix.length / 4);

  const anonymizedPrefix = emailPrefix
    .split('')
    .map((letter, i) => {
      if (i < lettersToKeep || letter === '+') {
        return letter;
      }
      return '*';
    })
    .join('');
  return `${anonymizedPrefix}@${domain}`;
};

const anonymizeEmailsInString = text => {
  if (!text || typeof text !== 'string') {
    return text;
  }
  const EMAIL_REGEX = new RegExp(
    // eslint-disable-next-line no-control-regex
    /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/,
    'g'
  );

  return text.replace(EMAIL_REGEX, email => anonymizeEmail(email));
};

module.exports = { removeTokenValue };
